import React, { useState, useEffect } from 'react';
import dataManager from '../utils/dataManager';
import useLoadMore from '../utils/useLoadMore';
import LoadMoreButton from './LoadMoreButton';
import { useAuth } from '../contexts/AuthContext';
import { API_ENDPOINTS } from '../config/appConfig';
import { getAuthHeaders } from '../utils/authApi';
import ConfigService from '../utils/configService';

const CommunityStories = () => {
  const { user } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStory, setNewStory] = useState({
    title: '',
    content: '',
    category: 'daily_practice',
    is_public: true
  });
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // 加载社区故事数据
  useEffect(() => {
    const loadStories = async () => {
      try {
        setLoading(true);
        setError(null);
        const storiesData = await dataManager.getCommunityStories();
        console.log('📚 社区故事数据:', storiesData);
        console.log('📚 故事分类配置:', storyCategories);

        // 检查数据中的分类
        if (Array.isArray(storiesData) && storiesData.length > 0) {
          const categories = [...new Set(storiesData.map(story => story.category))];
          console.log('📚 数据中的实际分类:', categories);

          // 详细检查第一个故事的数据结构
          const firstStory = storiesData[0];
          console.log('📚 第一个故事的完整数据:', firstStory);
          console.log('📚 故事标题:', firstStory.title);
          console.log('📚 故事内容:', firstStory.content);
          console.log('📚 故事作者:', firstStory.author);
          console.log('📚 故事分类:', firstStory.category);
          console.log('📚 故事标签:', firstStory.tags);
          console.log('📚 故事日期:', firstStory.date);
          console.log('📚 故事精选:', firstStory.featured);
          console.log('📚 故事点赞:', firstStory.likes);
        }

        setStories(Array.isArray(storiesData) ? storiesData : []);
      } catch (err) {
        console.error('Error loading community stories:', err);
        setError('加载社区故事失败，请确保Django服务器正在运行');
        setStories([]);
      } finally {
        setLoading(false);
      }
    };

    loadStories();
  }, []);

  // 从数据管理器中获取数据
  const storyCategories = dataManager.getStoryCategories();



  const filteredStories = selectedCategory === 'all'
    ? stories
    : stories.filter(story => story.category === selectedCategory);

  // 渐进式分页：首批 10 条，"加载更多"追加，切换分类时自动重置
  const {
    visibleItems: visibleStories,
    hasMore: hasMoreStories,
    remaining: remainingStories,
    loadMore: loadMoreStories,
  } = useLoadMore(filteredStories, 10, selectedCategory);

  const featuredStories = stories.filter(story => story.featured);

  // 处理添加新故事
  const getErrorMessage = (data, fallback) => {
    if (!data || typeof data !== 'object') return fallback;
    if (data.detail) return data.detail;
    if (data.non_field_errors?.[0]) return data.non_field_errors[0];

    const firstKey = Object.keys(data)[0];
    const firstValue = firstKey ? data[firstKey] : null;
    if (Array.isArray(firstValue) && firstValue[0]) {
      return `${firstKey}: ${firstValue[0]}`;
    }
    return fallback;
  };

  const handleAddStory = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!user) {
      alert('请先登录才能分享故事');
      return;
    }

    if (!newStory.title.trim() || !newStory.content.trim()) {
      alert('请填写完整的故事标题和内容');
      return;
    }

    try {
      setSubmitting(true);
      let savedStory;

      if (ConfigService.useServerData()) {
        const response = await fetch(ConfigService.getApiUrl(API_ENDPOINTS.STORIES), {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            title: newStory.title.trim(),
            content: newStory.content.trim(),
            category: newStory.category,
            tags: '',
            is_public: newStory.is_public,
          }),
        });
        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(getErrorMessage(responseData, '故事分享失败'));
        }

        savedStory = responseData;
        dataManager.invalidateCache('communityStories');
      } else {
        savedStory = {
          id: Date.now(),
          title: newStory.title.trim(),
          content: newStory.content.trim(),
          category: newStory.category,
          is_public: newStory.is_public,
          author: user.username,
          authorId: user.id,
          date: new Date().toISOString().split('T')[0],
          featured: false,
          likes: 0,
          createdAt: new Date().toISOString()
        };
        const existingStories = JSON.parse(localStorage.getItem('userStories') || '[]');
        existingStories.push(savedStory);
        localStorage.setItem('userStories', JSON.stringify(existingStories));
      }

      if (savedStory.is_public !== false) {
        setStories(prev => [savedStory, ...prev]);
      }

      alert('故事分享成功！感谢您的分享。');

      // 重置表单
      setNewStory({
        title: '',
        content: '',
        category: 'daily_practice',
        is_public: true
      });
      setShowAddForm(false);
    } catch (err) {
      console.error('Error saving story:', err);
      alert(err.message || '故事分享失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setNewStory(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex justify-between items-center mb-6">
          <div className="flex-1"></div>
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-emerald-800 mb-4">社区故事</h1>
            <p className="text-lg text-emerald-600 max-w-2xl mx-auto">
              分享你的瑜伽旅程，聆听他人的练习故事，在这里找到共鸣与启发
            </p>
          </div>
          <div className="flex-1 flex justify-end">
            {user ? (
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-emerald-600 text-white px-6 py-3 rounded-full hover:bg-emerald-700 transition-colors flex items-center space-x-2 shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>分享故事</span>
              </button>
            ) : (
              <div className="text-sm text-emerald-600 bg-emerald-50 px-4 py-2 rounded-full">
                登录后可分享故事
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Story Form */}
      {showAddForm && user && (
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-emerald-200">
          <h2 className="text-2xl font-bold text-emerald-800 mb-6">分享你的瑜伽故事</h2>
          <form onSubmit={handleAddStory} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-emerald-700 mb-2">
                故事标题 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newStory.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="给你的故事起个标题..."
                className="w-full px-4 py-3 border border-emerald-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                maxLength="100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-emerald-700 mb-2">
                故事分类
              </label>
              <select
                value={newStory.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-4 py-3 border border-emerald-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {storyCategories.filter(category => category.id !== 'all').map(category => (
                  <option key={category.id} value={category.id}>
                    {category.emoji} {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-emerald-700 mb-2">
                故事内容 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={newStory.content}
                onChange={(e) => handleInputChange('content', e.target.value)}
                placeholder="分享你的瑜伽体验、感悟或故事..."
                rows="6"
                className="w-full px-4 py-3 border border-emerald-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                maxLength="2000"
              />
              <div className="text-right text-sm text-gray-500 mt-1">
                {newStory.content.length}/2000
              </div>
            </div>

            <label className="flex items-start gap-3 rounded-lg border border-emerald-100 bg-emerald-50/50 p-4 text-sm text-emerald-800">
              <input
                type="checkbox"
                checked={newStory.is_public}
                onChange={(e) => handleInputChange('is_public', e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
              />
              <span>
                <span className="font-semibold">公开给其他人查看</span>
                <span className="block text-emerald-600">关闭后只会出现在“我的帖子”里。</span>
              </span>
            </label>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 bg-emerald-600 text-white py-3 px-6 rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
              >
                {submitting ? '发布中...' : '发布故事'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
              >
                取消
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Featured Stories */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-3xl p-8">
        <h2 className="text-2xl font-bold text-emerald-800 mb-6 flex items-center">
          <span className="mr-3 text-3xl">⭐</span>
          精选故事
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {featuredStories.slice(0, 4).map((story) => (
            <div key={story.id} className="bg-white/70 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-semibold text-emerald-800 flex-1">{story.title}</h3>
                <span className="text-2xl ml-2">
                  {storyCategories.find(cat => cat.id === story.category)?.emoji}
                </span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed mb-4 line-clamp-3">
                {story.content}
              </p>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-4">
                  <span className="text-emerald-600 font-medium">
                    {typeof story.author === 'object' ?
                      (story.author.name || story.author.username || '匿名作者') :
                      story.author || '匿名作者'}
                  </span>
                  <span className="text-gray-500">{story.date}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-red-500">❤️</span>
                  <span className="text-gray-600">{story.likes}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-white/70 rounded-2xl p-6">
        <h3 className="text-xl font-semibold text-emerald-800 mb-4">故事分类</h3>
        <div className="flex flex-wrap gap-3">
          {storyCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedCategory === category.id
                  ? 'bg-emerald-500 text-white shadow-lg'
                  : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
              }`}
            >
              <span className="mr-2">{category.emoji}</span>
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Stories List */}
      <div className="space-y-6">
        <h3 className="text-2xl font-bold text-emerald-800">
          {selectedCategory === 'all' ? '所有故事' : storyCategories.find(cat => cat.id === selectedCategory)?.name}
          <span className="text-lg font-normal text-emerald-600 ml-2">
            ({filteredStories.length} 个故事)
          </span>
        </h3>
        
        <div className="grid gap-6">
          {visibleStories.map((story) => (
            <div key={story.id} className="bg-white/70 rounded-2xl p-6 hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h4 className="text-xl font-semibold text-emerald-800 mb-2">{story.title}</h4>
                  <div className="flex items-center space-x-4 text-sm text-emerald-600">
                    <span className="flex items-center">
                      <span className="mr-1">
                        {storyCategories.find(cat => cat.id === story.category)?.emoji}
                      </span>
                      {storyCategories.find(cat => cat.id === story.category)?.name}
                    </span>
                    <span>by {typeof story.author === 'object' ?
                      (story.author.name || story.author.username || '匿名作者') :
                      story.author || '匿名作者'}</span>
                    <span>{story.date}</span>
                  </div>
                </div>
                {story.featured && (
                  <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                    精选
                  </span>
                )}
              </div>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                {story.content}
              </p>
              
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap gap-2">
                  {Array.isArray(story.tags) ? story.tags.map((tag, index) => (
                    <span key={index} className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs">
                      #{tag}
                    </span>
                  )) : null}
                </div>
                <div className="flex items-center space-x-4">
                  <button className="flex items-center space-x-1 text-gray-600 hover:text-red-500 transition-colors">
                    <span>❤️</span>
                    <span>{story.likes}</span>
                  </button>
                  <button className="text-emerald-600 hover:text-emerald-700 transition-colors">
                    分享
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <LoadMoreButton hasMore={hasMoreStories} remaining={remainingStories} onClick={loadMoreStories} />
      </div>

      {/* Add Story Button */}
      <div className="text-center">
        <button
          type="button"
          onClick={() => {
            if (!user) {
              alert('请先登录才能分享故事');
              return;
            }
            setShowAddForm(prev => !prev);
          }}
          className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-8 py-3 rounded-full font-medium hover:from-emerald-600 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          {showAddForm ? '取消分享' : '分享我的故事'}
        </button>
      </div>
    </div>
  );
};

export default CommunityStories;
